import { Alert, Box, Button, Chip, Grid, Skeleton, Stack, Typography } from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PlaceIcon from "@mui/icons-material/Place";
import { Link as RouterLink, useParams } from "react-router";
import { useOperator } from "../hooks/useApiQueries.js";
import { ContentCard } from "../components/ContentCard.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, NeonDivider, neonButtonSx } from "../components/Neon.js";
import { avatarInitial } from "../lib/displayHelpers.js";

export function OperatorDetail() {
  const { slug = "" } = useParams();
  const { data, isLoading } = useOperator(slug);
  if (isLoading) return <Skeleton variant="rectangular" height={300} />;
  if (!data) return <Alert severity="error">Operator not found.</Alert>;

  const op = data.operator;
  return (
    <>
      <SEO
        title={`${op.name} — operator`}
        description={op.bio ?? `${op.name} — earthmoving operator in ${op.region}`}
        canonical={`https://corianpark.au/operators/${op.slug}`}
      />

      {/* Header band */}
      <Box
        sx={{
          mx: { xs: -2, sm: -3 },
          mt: -4,
          mb: { xs: 4, md: 6 },
          py: { xs: 5, md: 7 },
          px: { xs: 2, sm: 3, md: 4 },
          background:
            "linear-gradient(135deg, rgba(255,176,0,0.10) 0%, rgba(255,136,0,0.02) 60%, transparent 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "100%",
            height: 3,
            background: "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)",
            boxShadow: "0 0 12px rgba(255,176,0,0.7)",
          }}
        />
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <NeonEyebrow glow>OPERATOR</NeonEyebrow>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ alignItems: { sm: "center" }, mt: 2 }}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                bgcolor: "primary.main",
                color: "common.black",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                fontSize: 44,
                flexShrink: 0,
                boxShadow: "0 0 30px rgba(255,176,0,0.6)",
              }}
            >
              {avatarInitial(op.name)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 32, md: 52 },
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                }}
              >
                {op.name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                {op.region && (
                  <Chip
                    icon={<PlaceIcon sx={{ fontSize: 16 }} />}
                    label={op.region}
                    sx={{ bgcolor: "background.paper" }}
                  />
                )}
                {op.postcode && (
                  <Chip
                    component={RouterLink}
                    to={`/built-near-you/${op.postcode}`}
                    label={op.postcode}
                    clickable
                    sx={{
                      bgcolor: "background.paper",
                      "&:hover": { borderColor: "primary.main", color: "primary.main" },
                    }}
                  />
                )}
              </Stack>
            </Box>
            <Button
              component={RouterLink}
              to={`/quote?operator=${op.slug}`}
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RequestQuoteIcon />}
              sx={{ ...neonButtonSx, flexShrink: 0 }}
            >
              Get a quote
            </Button>
          </Stack>
        </Box>
      </Box>

      {op.bio && (
        <Box sx={{ mb: 4, maxWidth: 760 }}>
          <NeonEyebrow>ABOUT</NeonEyebrow>
          <Typography variant="body1" sx={{ fontSize: 17, lineHeight: 1.7, mt: 1 }}>
            {op.bio}
          </Typography>
        </Box>
      )}

      {op.equipmentSummary && (
        <Box sx={{ mb: 4 }}>
          <NeonEyebrow>RUNNING</NeonEyebrow>
          <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
            {op.equipmentSummary}
          </Typography>
        </Box>
      )}

      <NeonDivider sx={{ my: 4 }} />

      <NeonEyebrow>THEIR CONTENT</NeonEyebrow>
      <Typography variant="h3" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, mb: 3 }}>
        Articles &amp; videos featuring {op.name}
      </Typography>
      {data.content.length === 0 ? (
        <Alert severity="info">No content from this operator yet.</Alert>
      ) : (
        <Grid container spacing={2}>
          {data.content.map((c) => (
            <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <ContentCard item={c} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}
